import * as React from "react";
import { useContext, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import { Grid } from "@mui/material";
import dayjs from "dayjs";
import { TicketProps } from "../FlightPackage/Ticket/FlightLegs/ticketFlightLegs";
import { GlobalContext } from "../Utils/Contexts";
import Signature from "./inputs/signature";
import StringEditor from "./inputs/stringEditor";
import EmailAddressEditor from "./inputs/emailAddressEditor";
import { CUSTOMER_SIGNATURE } from "../Sync/useSignableResourceTypeBackgroundPull";
import { Signable } from "../sharedCode/avroGraphql";
import { mergeTicketSignable } from "../Utils/TicketDatabase";
import { IsNullOrEmpty } from "../Utils/UtilityMethods";
import { FIELD_LIMITS } from "../sharedCode/fieldLimits";

interface State {
    signableId: number;
    signature: string | undefined;
    name: string | undefined;
    title: string | undefined;
    email: string | undefined;
}

interface Props extends TicketProps {
    setShowApprove(shouldShow: boolean): void;
}

export default function TicketPdfSignature(props: Props): JSX.Element {
    const context = useContext(GlobalContext);

    const [state, setState] = useState({} as State);

    return (
        <Dialog open={true}>
            <Grid container sx={{ p: 2 }}>
                <Grid container item xs={12} justifyContent={"center"}>
                    <Typography component="h1" variant="h6">
                        Sign Here
                    </Typography>
                </Grid>
                <Grid container item xs={12} sx={{ pt: 2 }}>
                    <Grid item flex={1}></Grid>
                    <Signature
                        initialValue={state.signature}
                        sourceId={state.signableId}
                        sourceProperty={"signature"}
                        labelText={"n/a"}
                        isRequired={true}
                        mergeChanges={mergeSignableChanges}
                        errors={props.errors}
                        containerUrl={"pdf/sign"}
                        isDisabled={props.isReadOnly}
                    />
                    <Grid item flex={1}></Grid>
                </Grid>
                <Grid item xs={12} justifyContent={"center"} sx={{ pt: 2, pr: 3, pb: 0, pl: 3 }}>
                    <StringEditor
                        labelText="NAME"
                        initialValue={state.name ?? ""}
                        sourceId={1}
                        sourceProperty={"name"}
                        isDisabled={props.isReadOnly}
                        isRequired={true}
                        mergeChanges={mergeSignableChanges}
                        errors={props.errors}
                        containerUrl={"pdf/sign"}
                        maxLength={FIELD_LIMITS.signatory}
                    />
                </Grid>
                <Grid item xs={12} justifyContent={"center"} sx={{ pt: 0, pr: 3, pb: 0, pl: 3 }}>
                    <StringEditor
                        labelText="TITLE"
                        initialValue={state.title ?? ""}
                        sourceId={1}
                        sourceProperty={"title"}
                        isDisabled={props.isReadOnly}
                        isRequired={true}
                        mergeChanges={mergeSignableChanges}
                        errors={props.errors}
                        containerUrl={"pdf/sign"}
                        maxLength={FIELD_LIMITS.signatory_job_title}
                    />
                </Grid>
                <Grid item xs={12} justifyContent={"center"} sx={{ pt: 0, pr: 3, pb: 2, pl: 3 }}>
                    <EmailAddressEditor
                        labelText="EMAIL ADDRESS"
                        initialValue={state.email ?? ""}
                        sourceId={1}
                        sourceProperty={"email"}
                        isDisabled={props.isReadOnly}
                        isRequired={true}
                        mergeChanges={mergeSignableChanges}
                        errors={props.errors}
                        containerUrl={"pdf/sign"}
                        maxLength={FIELD_LIMITS.signatory_email}
                    />
                </Grid>
                <Grid container item xs={12} justifyContent={"center"}>
                    <Button variant="contained" sx={{ m: 1 }} onClick={handleClose}>
                        CANCEL
                    </Button>
                    <Button variant="contained" sx={{ m: 1 }} onClick={handleClearSignature}>
                        CLEAR SIGNATURE
                    </Button>
                    <Button variant="contained" sx={{ m: 1 }} onClick={handleSaveSignature} disabled={disableSave()}>
                        ATTACH SIGNATURE
                    </Button>
                </Grid>
            </Grid>
        </Dialog>
    );

    function handleClose(): void {
        props.setShowApprove(false);
    }

    function handleClearSignature(): void {
        setState((prev) => {
            return { ...prev, signature: "CLEAR" };
        });
    }

    async function handleSaveSignature(): Promise<void> {
        const signable = {
            date: dayjs().toISOString(),
            id: state.signableId === undefined ? props.ticket.nextLocalId : state.signableId,
            model: "Ticket",
            signable_resource_type_id: CUSTOMER_SIGNATURE,
            signatory: state.name,
            signatory_job_title: state.title,
            signatory_email: state.email,
            signature: state.signature,
            ref_id: props.ticket.id,
        } as Signable;

        const result = await mergeTicketSignable(props.ticket.id, signable);

        context.setShouldPushTickets(true);

        handleClose();
    }

    function disableSave(): boolean {
        return IsNullOrEmpty(state.name) || IsNullOrEmpty(state.signature);
    }

    function mergeSignableChanges(signableId: number, changes: Record<string, unknown>) {
        setState((prev) => {
            return { ...prev, ...changes };
        });
    }
}
